import {
    CoreLogger,
    DalValue,
    ExtensionAPI,
    Namespace,
    Null,
    PAGE_FRAGMENT_TYPE,
    pointerUtils,
    SnapshotDal
} from '@wix/document-manager-core'
import type {ConnectionList, ControllerConnectionItem, Experiment, Pointer} from '@wix/document-services-types'
import _ from 'lodash'
import type {Namespaces} from '../../types'
import type {SafeRemovalError, SafeRemovalResult, SchemaAPI, SchemaExtensionAPI} from '../schema/schema'
import {removeExclusions, removeInfraExclusions} from './csaveDataRules'
import {Action, ActionOperation, ActionTransientData} from './serverProtocolTypes'
import {ReportableError} from '@wix/document-manager-utils'
import type {RelationshipsAPI} from '../relationships'
import {DistributorAPI, DistributorExtensionAPI, pendingMessagesPointerType} from '../distributor/distributor'
import type {DistributeMessage} from '../distributor/messages'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'

const {getPointer, getSignaturePointer} = pointerUtils

const FRAGMENT_INDEX_TYPE = 'FragmentIndex'
const FRAGMENT_INDEX_DATA_TYPE = 'FragmentIndexDataItem'

const getOp = (value: any, oldValue: any): ActionOperation => {
    if (oldValue === undefined) {
        return ActionOperation.ADD
    }
    if (value === undefined) {
        return ActionOperation.REMOVE
    }
    return ActionOperation.REPLACE
}

const getOldValue = (prev: Null<SnapshotDal>, namespace: string, id: string, fragmentType?: string) =>
    prev?.getValue({type: namespace, id, fragmentType})

const getPageId = (val: any) => _.get(val, ['metaData', 'pageId'])

interface ConvertResult {
    result?: Action[]
    error?: SafeRemovalError
    atomicResult?: Record<string, Action[]>
}

function removeWhitelistedPropsAndValidate(
    filtered: Namespaces,
    schemaService: SchemaAPI,
    logger: CoreLogger
): SafeRemovalResult {
    const conservativeRemoval = true
    const removeWhitelistedPropertiesSafelyResult: Record<
        string,
        Record<string, any>
    > = schemaService.removeWhitelistedPropertiesSafely(filtered, conservativeRemoval)
    let hadError = false
    _.forEach(removeWhitelistedPropertiesSafelyResult, (namespaceData, namespaceName) => {
        _.forEach(namespaceData, data => {
            if (!data) {
                return
            }
            if (schemaService.hasNamespace(namespaceName)) {
                try {
                    schemaService.validate(data.type, data, namespaceName)
                } catch (error: any) {
                    hadError = true
                    _.forEach(error.errors, validationError => {
                        logger.captureError(
                            new ReportableError({
                                message: validationError.message,
                                errorType: 'PostWhitelistError',
                                tags: {},
                                extras: {
                                    type: data.type,
                                    validationError,
                                    data
                                }
                            })
                        )
                    })
                }
            }
        })
    })
    if (hadError) {
        return removeProps(filtered, false, schemaService, logger)
    }
    return {result: removeWhitelistedPropertiesSafelyResult, error: null}
}

function removeProps(
    filtered: Namespaces,
    whitelistRemovalExperiment: boolean,
    schemaService: SchemaAPI,
    logger: CoreLogger
): SafeRemovalResult {
    if (whitelistRemovalExperiment) {
        return removeWhitelistedPropsAndValidate(filtered, schemaService, logger)
    }

    const removeAdditionalPropertiesSafelyResult = schemaService.removeAdditionalPropertiesSafely(filtered)

    return _.pick(removeAdditionalPropertiesSafelyResult, ['error', 'result'])
}

const getOwingComponentIdIfApplicable = (
    relationships: RelationshipsAPI,
    id: string,
    namespace: Namespace
): string | undefined => {
    const dalItemPointer: Pointer = getPointer(id, namespace)
    const owningComp = relationships.relationships.getOwningComponentToPointer(dalItemPointer)
    return owningComp?.id
}

const getConnectionControllerIdToAppIdMap = (
    dataModel: DataModelExtensionAPI,
    value: any,
    logger: CoreLogger
): Record<string, string> | undefined => {
    const controllerIds: Record<string, string> = {}
    try {
        if (value?.type === 'ConnectionList') {
            const connectionList = value as ConnectionList
            const connectionItems = connectionList.items?.filter(
                (item: {type: string}) => item.type === 'ConnectionItem'
            ) as ControllerConnectionItem[]
            connectionItems.forEach(connectionItem => {
                const {controllerId} = connectionItem
                const {applicationId} = dataModel.dataModel.getItem(controllerId, 'data', value.metaData?.pageId)
                controllerIds[controllerId] = applicationId
            })
        }
    } catch (e: any) {
        logger.captureError(
            new ReportableError({
                errorType: 'errorCreatingConnectionControllerToAppIdMap',
                message: e.message
            })
        )
    }
    return _.isEmpty(controllerIds) ? undefined : controllerIds
}

const getActionTransientData = (
    extensionAPI: ExtensionAPI,
    id: string,
    namespace: Namespace,
    value: any,
    logger: CoreLogger
): ActionTransientData => {
    const relationships: RelationshipsAPI = extensionAPI as RelationshipsAPI
    const dataModel = extensionAPI as DataModelExtensionAPI
    return {
        owningComponentId: getOwingComponentIdIfApplicable(relationships, id, namespace),
        connectionControllerIdToAppIdMap: getConnectionControllerIdToAppIdMap(dataModel, value, logger)
    }
}
const extractPendingMessages = (data: Namespaces) => {
    const pendingMessagesData = data[pendingMessagesPointerType]
    if (!pendingMessagesData) {
        return []
    }
    return _(pendingMessagesData)
        .values()
        .compact()
        .map(p => ({message: p?.message, options: p?.options}))
        .value()
}
const handlePendingDistributedMessage = (distributor: DistributorAPI, data: Namespaces, correlationId: string) => {
    const pendingMessages = extractPendingMessages(data)
    _.forEach(pendingMessages, ({message}) =>
        distributor.attachMessageToTransactionApproval(message as unknown as DistributeMessage, correlationId)
    )
}

const createActions = (
    data: Namespaces,
    prev: Null<SnapshotDal>,
    current: SnapshotDal,
    extensionAPI: ExtensionAPI,
    logger: CoreLogger,
    fragmentType?: string
): Action[] => {
    return _.flatMap(data, (v: Record<string, DalValue>, namespace: string) =>
        _.flatMap(v, (value: DalValue, id: string) => {
            const oldValue = getOldValue(prev, namespace, id, fragmentType)
            const op = getOp(value, oldValue)
            const basedOnSignaturePointer = getSignaturePointer(getPointer(id, namespace, {fragmentType}))
            const basedOnSignature = prev?.getValue(basedOnSignaturePointer)
            const sig = current?.getValue(basedOnSignaturePointer)
            const isPageFragment = fragmentType === PAGE_FRAGMENT_TYPE || !fragmentType

            const action = {
                op,
                id,
                namespace,
                value: value ?? null,
                basedOnSignature,
                sig
            }

            if (isPageFragment) {
                const oldPage = getPageId(oldValue)
                const newPage = getPageId(value)
                const previousPageId = oldPage === newPage ? undefined : oldPage

                _.merge(action, {
                    previousPageId,
                    transientData: getActionTransientData(extensionAPI, id, namespace, value, logger)
                })
            } else {
                _.merge(action, {
                    dataItemType: fragmentType === FRAGMENT_INDEX_TYPE ? FRAGMENT_INDEX_DATA_TYPE : value?.type,
                    fragmentType,
                    fragmentName: value?.metaData?.fragmentInstance ?? oldValue?.metaData?.fragmentInstance
                })
            }

            if (fragmentType === FRAGMENT_INDEX_TYPE && action.op === ActionOperation.REPLACE) {
                logger.captureError(
                    new ReportableError({
                        errorType: 'forbiddenFragmentIndexOperation',
                        message: 'sending an update operation for FragmentIndex',
                        extras: {
                            namespace: action.namespace,
                            value: action.value,
                            oldValue
                        }
                    })
                )
            }

            return action
        })
    )
}

const handleSafeRemovalResult = (result: SafeRemovalResult | Action[]): ConvertResult =>
    _.has(result, ['error']) ? {error: (result as SafeRemovalResult).error!} : {result: result as Action[]}

export const createActionsData = (
    data: Namespaces,
    prev: Null<SnapshotDal>,
    current: SnapshotDal,
    logger: CoreLogger,
    experimentInstance: Experiment,
    extensionAPI: ExtensionAPI,
    fragmentType?: string
): Action[] | SafeRemovalResult => {
    const schemaService: SchemaAPI = (extensionAPI as SchemaExtensionAPI).schemaAPI
    const {distributor} = extensionAPI as DistributorExtensionAPI
    handlePendingDistributedMessage(distributor, data, current.id)
    const filtered =
        !fragmentType || fragmentType === PAGE_FRAGMENT_TYPE ? removeExclusions(data) : removeInfraExclusions(data)
    const filteredKeys = Object.keys(filtered)
    const removedNamespaces = _.difference(Object.keys(data), filteredKeys)
    logger.interactionStarted('removeExclusions', {
        tags: {id: current.id},
        extras: {
            removedNamespaces
        }
    })

    let filteredData = filtered
    const isWhitelistRemoval = experimentInstance.isOpen('dm_useWhitelistRemovalForCsave')

    if (!fragmentType || fragmentType === PAGE_FRAGMENT_TYPE) {
        const removalResult = removeProps(filtered, isWhitelistRemoval, schemaService, logger)
        if (removalResult.error) {
            return removalResult
        }
        filteredData = removalResult.result as Namespaces
    }

    const newData: Action[] = createActions(filteredData, prev, current, extensionAPI, logger, fragmentType)

    if (!newData.length) {
        logger.interactionStarted('emptyTransaction', {
            tags: {id: current.id},
            extras: {
                filteredKeys,
                removedNamespaces,
                isWhitelistRemoval
            }
        })
    }

    return newData
}

export const convertFragmentsData = (
    prev: Null<SnapshotDal>,
    current: SnapshotDal,
    logger: CoreLogger,
    experimentInstance: Experiment,
    extensionAPI: ExtensionAPI
): ConvertResult => {
    const fragmentDiffs = current.fragmentMutableDiff(prev) as Record<string, Namespaces>
    const atomicActions: Record<string, Action[]> = {}
    const orderedKeys = [
        ...[FRAGMENT_INDEX_TYPE].filter(key => key in fragmentDiffs),
        ...Object.keys(fragmentDiffs).filter(key => key !== FRAGMENT_INDEX_TYPE)
    ]
    for (const fragmentType of orderedKeys) {
        const diff = fragmentDiffs[fragmentType]
        const result = createActionsData(diff, prev, current, logger, experimentInstance, extensionAPI, fragmentType)
        const {error, result: actions} = handleSafeRemovalResult(result)
        if (error) {
            return {error}
        }
        atomicActions[fragmentType] = actions ?? []
    }

    return {
        atomicResult: _.mapValues(atomicActions, _actions =>
            _actions.map(_action => {
                if (_action.value && _.has(_action, ['fragmentType']) && _action.fragmentType !== PAGE_FRAGMENT_TYPE) {
                    const {metaData} = _action.value ?? {}
                    const rest = _.omit(_action.value, ['metaData'])
                    return {
                        ..._action,
                        value: rest,
                        metadata: metaData
                    }
                }
                return _action
            })
        )
    }
}
